import React, { useState, useRef } from "react";
import "./basic_python_course.css";
import useSEO from "../../../hooks/useSEO";

/* ═══════════════════════════════════════════════════════
   COMPLETE PYTHON COURSE — 16 Topics, all subtopics
═══════════════════════════════════════════════════════ */
const COURSE = [
  {
    id: 1, title: "Introduction to Python", icon: "🐍",
    subtopics: [
      {
        id: "1-1", title: "What is Python?", duration: "4 min",
        content: {
          heading: "What is Python?",
          intro: "Python is a high-level, easy-to-learn programming language used to build software, automate tasks, and analyze data. Python lets you give instructions to a computer in simple English-like syntax.",
          blocks: [
            { type: "code", label: "💡 Your very first Python line:", code: 'print("Hello, World!")' },
            { type: "points", label: "🔍 Key Characteristics:", items: ["Easy to read & write — looks like English", "Interpreted language — no complex compilation needed", "Cross-platform — runs on Windows, Mac, and Linux", "Open-source — completely free to use"] },
            { type: "tip-box", text: "Python is perfect for beginners because its syntax is clean and readable. You can focus on learning to code, not on complex rules!" },
          ],
        },
      },
      {
        id: "1-2", title: "Why Python is popular", duration: "4 min",
        content: {
          heading: "Why Python is So Popular?",
          intro: "Python is one of the most popular programming languages in the world — and for very good reasons!",
          blocks: [
            { type: "card-grid", items: [
              { emoji: "🤝", title: "Beginner Friendly", desc: "Very simple syntax. Easy for non-technical people to start. Feels natural to write." },
              { emoji: "🌍", title: "Huge Community", desc: "Millions of developers worldwide. Easy to find tutorials, solutions, and help online." },
              { emoji: "📦", title: "Powerful Libraries", desc: "AI → TensorFlow, Data → Pandas, Web → Django. Don't build from scratch!" },
              { emoji: "💼", title: "High Job Demand", desc: "Used in top companies. High-paying roles in AI, Data Science, and Backend development." },
              { emoji: "🔀", title: "Versatile", desc: "One language = many uses. No need to learn multiple languages as a beginner." },
            ]},
            { type: "tip-box", text: "Python is consistently ranked #1 or #2 in programming language popularity indexes worldwide!" },
          ],
        },
      },
      {
        id: "1-3", title: "Real-world uses", duration: "5 min",
        content: {
          heading: "Real-World Uses of Python",
          intro: "Python is used across many industries. Once you learn it, you'll start seeing it everywhere!",
          blocks: [
            { type: "card-grid", items: [
              { emoji: "🤖", title: "AI & Machine Learning", desc: "Face recognition, chatbots, recommendation systems. Used by Google, Netflix, Tesla." },
              { emoji: "🌐", title: "Web Development", desc: "Build websites and backends with Django and Flask. Instagram backend uses Python!" },
              { emoji: "📊", title: "Data Science", desc: "Data analysis, visualization, business insights. Used by analysts to make key decisions." },
              { emoji: "⚙️", title: "Automation 🔥", desc: "Automate emails, rename files, web scraping. Saves hours of manual work every day." },
              { emoji: "🎮", title: "Game Development", desc: "Simple games with Pygame. Desktop apps and IoT projects." },
              { emoji: "🔬", title: "Scientific Research", desc: "Used in physics, biology, finance. Complex calculations made simple." },
            ]},
          ],
        },
      },
      {
        id: "1-4", title: "Python vs other languages", duration: "4 min",
        content: {
          heading: "Python vs Other Languages",
          intro: "Understanding how Python compares helps you appreciate why it's the best starting point for beginners.",
          blocks: [
            { type: "compare-table", headers: ["Feature", "Python", "C++"],
              rows: [["Syntax", "Easy & readable", "Complex"], ["Speed", "Slower", "Very Fast"], ["Best for", "AI, Data, Web", "Game engines, OS"]] },
            { type: "compare-table", headers: ["Feature", "Python", "Java"],
              rows: [["Code Length", "Short (5 lines)", "Long (20+ lines)"], ["Learning Curve", "Easy", "Moderate"], ["Best for", "AI, scripting", "Enterprise apps"]] },
            { type: "compare-table", headers: ["Feature", "Python", "JavaScript"],
              rows: [["Main Use", "Backend, AI, Data", "Frontend (web browser)"], ["Syntax", "Clean & simple", "Event-driven"], ["Environment", "Server, scripts", "Browser"]] },
            { type: "tip-box", text: "Python is easier than C++, shorter than Java, and more versatile than JavaScript. Perfect for your first language!" },
          ],
        },
      },
    ],
  },
  {
    id: 2, title: "Installing Python & Setup", icon: "⚙️",
    subtopics: [
      {
        id: "2-1", title: "Install Python (Windows/Mac)", duration: "5 min",
        content: {
          heading: "Install Python on Windows & Mac",
          intro: "Before writing Python code, you need Python installed on your computer. Follow these steps carefully.",
          blocks: [
            { type: "steps", label: "💻 Windows Installation:", items: ["Go to python.org → click Downloads", "Click 'Download Python (latest version)'", "Run the installer file", "⚠️ IMPORTANT: Tick 'Add Python to PATH' checkbox!", "Click 'Install Now' and wait"] },
            { type: "steps", label: "🍎 Mac Installation:", items: ["Go to python.org and download the .pkg file", "Double-click to run the installer", "OR use Terminal: brew install python"] },
            { type: "code", label: "✅ Verify Installation — open Terminal/CMD and type:", code: "python --version\n# or\npython3 --version\n# You should see: Python 3.x.x" },
            { type: "tip-box", text: "⚠️ The most common mistake: forgetting to tick 'Add Python to PATH'. If Python isn't found, reinstall and tick that box!" },
          ],
        },
      },
      {
        id: "2-2", title: "Install Visual Studio Code", duration: "4 min",
        content: {
          heading: "Install Visual Studio Code",
          intro: "VS Code is a free, powerful code editor used by millions of developers worldwide. It's the industry standard.",
          blocks: [
            { type: "steps", label: "📥 Installation Steps:", items: ["Go to code.visualstudio.com", "Download for your OS (Windows/Mac/Linux)", "Install normally (Next → Next → Finish)", "Launch VS Code"] },
            { type: "steps", label: "🔌 Install Python Extension (IMPORTANT!):", items: ["Open VS Code", "Click Extensions icon in left sidebar (or Ctrl+Shift+X)", "Search for 'Python'", "Install the extension by Microsoft", "Restart VS Code"] },
            { type: "card-grid", items: [
              { emoji: "💡", title: "Code Suggestions", desc: "Auto-complete as you type Python code" },
              { emoji: "▶️", title: "Run Button", desc: "Run Python files with one click" },
              { emoji: "🐛", title: "Debugging", desc: "Find and fix errors with built-in debugger" },
            ]},
          ],
        },
      },
      {
        id: "2-3", title: "Running first Python program", duration: "4 min",
        content: {
          heading: "Running Your First Python Program",
          intro: "Let's create and run your very first Python program! This is the moment every developer remembers.",
          blocks: [
            { type: "steps", label: "🧪 Step-by-Step:", items: ["Open VS Code", "Click 'New File' (Ctrl+N)", "Save the file as: hello.py (File → Save As)", "Type the code below", "Run: click ▶️ Run button OR open Terminal and type: python hello.py"] },
            { type: "code", label: "✅ Your First Program:", code: 'print("Hello, World!")' },
            { type: "code", label: "📌 Expected Output:", code: "Hello, World!" },
            { type: "tip-box", text: "🎉 Congratulations! If you see 'Hello, World!' in the terminal, your Python setup is working perfectly!" },
          ],
        },
      },
      {
        id: "2-4", title: "Python IDLE vs VS Code", duration: "3 min",
        content: {
          heading: "Python IDLE vs VS Code",
          intro: "Python comes with a built-in editor called IDLE. But VS Code is much more powerful. Here's the comparison:",
          blocks: [
            { type: "compare-table", headers: ["Feature", "Python IDLE", "VS Code"],
              rows: [["Ease of use", "Very Easy", "Easy"], ["Features", "Basic", "Advanced & Professional"], ["Best for", "Quick testing", "Real projects"], ["Extensions", "❌ No", "✅ Yes, hundreds"], ["Debugging", "Limited", "Powerful built-in"]] },
            { type: "tip-box", text: "Start with Python IDLE for basics if you want the simplest experience. But our course recommends VS Code — it's the industry standard used in real jobs!" },
          ],
        },
      },
    ],
  },
  {
    id: 3, title: "First Python Program", icon: "🖨️",
    subtopics: [
      {
        id: "3-1", title: "print() function", duration: "4 min",
        content: {
          heading: "The print() Function",
          intro: "The print() function displays output on the screen. It is the first and most important function beginners learn in Python.",
          blocks: [
            { type: "code", label: "✅ Basic Examples:", code: '# Printing text\nprint("Hello, World!")\n\n# Printing a number\nprint(10)\n\n# Printing multiple values\nprint("Age:", 25)\n\n# Using a variable\nname = "Amit"\nprint("Hello", name)' },
            { type: "points", label: "⚡ Important Rules:", items: ["Always use quotes for text → \"Hello\" or 'Hello'", "No quotes needed for numbers → print(10)", "Use commas to print multiple values → print(\"Age:\", 25)", "Each print() starts on a new line automatically"] },
            { type: "tip-box", text: "print() is your best debugging friend! When you're not sure what's happening in your code, add print() statements to inspect values." },
          ],
        },
      },
      {
        id: "3-2", title: "Comments in Python", duration: "3 min",
        content: {
          heading: "Comments in Python",
          intro: "Comments are notes in your code that Python ignores when running the program. They explain what your code does — for you and other developers.",
          blocks: [
            { type: "code", label: "✅ Single-Line Comment:", code: '# This is a comment — Python ignores this line\nprint("Hello")  # You can also comment at end of line' },
            { type: "code", label: "✅ Multi-Line Comment:", code: '"""\nThis is a multi-line comment\nUsed for longer explanations\nand documentation\n"""\nprint("Python")' },
            { type: "points", label: "🎯 Why Comments Matter:", items: ["Help you remember what code does later", "Help other developers understand your logic", "Useful for debugging — 'comment out' code to test", "Professional code always has good comments"] },
          ],
        },
      },
      {
        id: "3-3", title: "Syntax basics", duration: "5 min",
        content: {
          heading: "Python Syntax Basics",
          intro: "Syntax = rules of writing code. Just like grammar in English, Python has strict rules you must follow.",
          blocks: [
            { type: "code", label: "🔑 Rule 1 — Indentation (VERY IMPORTANT 🔥):", code: '# Python uses spaces/indentation instead of {}\n# This is CORRECT:\nif True:\n    print("Correct indentation")\n\n# This is WRONG:\nif True:\nprint("Missing indent")  # ❌ IndentationError' },
            { type: "code", label: "🔑 Rule 2 — Case Sensitivity:", code: 'print("Hello")  # ✅ Correct\nPrint("Hello")  # ❌ Error — capital P\nPRINT("Hello")  # ❌ Error' },
            { type: "code", label: "🔑 Rule 3 — Quotes Must Match:", code: 'print("Hello")   # ✅ Double quotes\nprint(\'Hello\')   # ✅ Single quotes\nprint("Hello\')   # ❌ Error — mismatched!' },
            { type: "tip-box", text: "Python does NOT need semicolons (;) at the end of lines. Unlike Java or C++, you just press Enter and write the next line!" },
          ],
        },
      },
      {
        id: "3-4", title: "Common beginner errors", duration: "4 min",
        content: {
          heading: "Common Beginner Errors",
          intro: "Every programmer makes these mistakes. Knowing them in advance will save you hours of frustration!",
          blocks: [
            { type: "code", label: "❌ Error 1 — SyntaxError (Missing bracket):", code: 'print("Hello"   # ❌ Missing closing bracket\nprint("Hello")  # ✅ Correct' },
            { type: "code", label: "❌ Error 2 — IndentationError:", code: 'if True:\nprint("Hello")    # ❌ No indent\n\nif True:\n    print("Hello")  # ✅ 4 spaces indent' },
            { type: "code", label: "❌ Error 3 — NameError (Variable not defined):", code: 'print(name)  # ❌ name not defined yet!\n\nname = "Amit"\nprint(name)  # ✅ Define first, then use' },
            { type: "code", label: "❌ Error 4 — Typo in function name:", code: 'pritn("Hello")  # ❌ Typo!\nprint("Hello")  # ✅ Correct spelling' },
            { type: "tip-box", text: "Always read the error message carefully! Python tells you exactly what went wrong and on which line. The error message is your friend, not your enemy." },
          ],
        },
      },
    ],
  },
  {
    id: 4, title: "Variables & Data Types", icon: "📦",
    subtopics: [
      {
        id: "4-1", title: "Variables", duration: "4 min",
        content: {
          heading: "Variables in Python",
          intro: "A variable is like a labeled box that stores data in your program. You give it a name, put data inside, and use it later.",
          blocks: [
            { type: "code", label: "✅ Creating Variables:", code: 'name = "Amit"    # stores text\nage = 25         # stores number\nheight = 5.8     # stores decimal\nis_student = True  # stores True/False' },
            { type: "code", label: "🔑 Variable Naming Rules:", code: '# ✅ ALLOWED:\nuser_name = "Amit"\nage1 = 20\nstudent_marks = 85\n\n# ❌ NOT ALLOWED:\n1name = "Amit"     # starts with number\nuser-name = "Amit" # hyphen not allowed\nclass = "10"       # reserved keyword' },
            { type: "tip-box", text: "Best Practice: Use meaningful names (student_name ✅ not x) and lowercase with underscores (user_age ✅ not UserAge for variables)." },
          ],
        },
      },
      {
        id: "4-2", title: "int and float", duration: "4 min",
        content: {
          heading: "int and float Data Types",
          intro: "int stores whole numbers. float stores decimal numbers. Both are used for mathematical calculations.",
          blocks: [
            { type: "code", label: "🟢 Integer (int) — Whole Numbers:", code: "age = 25\nmarks = 90\nyear = 2026\nnegative = -10\n\nprint(type(age))  # <class 'int'>" },
            { type: "code", label: "🟢 Float — Decimal Numbers:", code: "price = 99.99\nheight = 5.8\npi = 3.14159\n\nprint(type(price))  # <class 'float'>\n\n# Division always gives float:\nresult = 10 / 2\nprint(result)  # 5.0 (float, not 5)" },
            { type: "compare-table", headers: ["Type", "Example", "Contains"],
              rows: [["int", "age = 25", "Whole numbers (no decimal)"], ["float", "price = 99.99", "Decimal numbers"]] },
          ],
        },
      },
      {
        id: "4-3", title: "string", duration: "4 min",
        content: {
          heading: "String (str) Data Type",
          intro: "A string is text — any sequence of characters. Strings must always be inside quotes.",
          blocks: [
            { type: "code", label: "✅ String Examples:", code: 'name = "Amit"\ncity = \'Ahmedabad\'\nmessage = "Hello World 123!"\n\nprint(type(name))  # <class \'str\'>' },
            { type: "code", label: "🔍 String Operations:", code: '# Joining (concatenation)\nfirst = "Hello"\nsecond = "World"\nprint(first + " " + second)  # Hello World\n\n# Length\nprint(len("Python"))  # 6\n\n# Repetition\nprint("Hi " * 3)  # Hi Hi Hi' },
            { type: "tip-box", text: "Strings can contain letters, numbers, and symbols — anything in quotes. You cannot mix strings and numbers directly: print(\"Age: \" + 25) gives an error!" },
          ],
        },
      },
      {
        id: "4-4", title: "boolean", duration: "3 min",
        content: {
          heading: "Boolean (bool) Data Type",
          intro: "Boolean has only two possible values: True or False. It is the foundation of all decision-making in programming.",
          blocks: [
            { type: "code", label: "✅ Boolean Examples:", code: "is_student = True\nis_logged_in = False\nhas_paid = True\n\nprint(type(is_student))  # <class 'bool'>" },
            { type: "points", label: "📌 Important Rules:", items: ["Always capitalize: True and False (not true/false)", "Used in conditions: if is_student == True:", "Comparison operators return boolean: 5 > 3 returns True", "Used with logical operators: and, or, not"] },
            { type: "code", label: "💡 Booleans from Comparisons:", code: "print(5 > 3)   # True\nprint(5 < 3)   # False\nprint(5 == 5)  # True\nprint(5 != 3)  # True" },
          ],
        },
      },
      {
        id: "4-5", title: "Type checking (type())", duration: "3 min",
        content: {
          heading: "Type Checking with type()",
          intro: "The type() function tells you what type of data is stored in a variable. Essential for debugging!",
          blocks: [
            { type: "code", label: "✅ Checking Types:", code: "age = 25\nname = \"Amit\"\nprice = 99.9\nis_valid = True\n\nprint(type(age))      # <class 'int'>\nprint(type(name))     # <class 'str'>\nprint(type(price))    # <class 'float'>\nprint(type(is_valid)) # <class 'bool'>" },
            { type: "code", label: "🔄 Type Conversion (Important 🔥):", code: '# String to Integer\nx = "10"\ny = int(x)\nprint(y + 5)  # 15\n\n# Integer to String\nnum = 100\ntext = str(num)\nprint("Number: " + text)  # Number: 100\n\n# String to Float\nf = float("3.14")\nprint(f)  # 3.14' },
            { type: "tip-box", text: "Dynamic Typing: Python automatically decides the data type. x = 10 (int), then x = 'Hello' (now string). Same variable, different types!" },
          ],
        },
      },
    ],
  },
  {
    id: 5, title: "User Input & Output", icon: "⌨️",
    subtopics: [
      {
        id: "5-1", title: "input() function", duration: "4 min",
        content: {
          heading: "The input() Function",
          intro: "The input() function lets your program ask the user for data. This makes programs interactive!",
          blocks: [
            { type: "code", label: "✅ Basic Input:", code: 'name = input("Enter your name: ")\nprint("Hello", name)\n\n# How it works:\n# 1. Program shows: Enter your name:\n# 2. User types: Amit\n# 3. Stored in: name variable\n# 4. Output: Hello Amit' },
            { type: "code", label: "⚠️ IMPORTANT — input() ALWAYS returns string:", code: 'age = input("Enter age: ")\nprint(type(age))  # <class \'str\'> — always!\n\n# Even if user enters 25, it stores "25" (string)\n# This causes problems with math!' },
            { type: "tip-box", text: "Remember: input() always returns a string. Even if the user types 100, Python stores it as '100' (text). You must convert it for calculations!" },
          ],
        },
      },
      {
        id: "5-2", title: "Type casting (int, float)", duration: "4 min",
        content: {
          heading: "Type Casting — Converting Input Types",
          intro: "Since input() always returns a string, you need to convert it for mathematical operations. This is called type casting.",
          blocks: [
            { type: "code", label: "❌ Problem Without Type Casting:", code: 'age = input("Enter age: ")  # stores "25" as string\nprint(age + 5)  # ❌ TypeError: cannot add str + int' },
            { type: "code", label: "✅ Solution — Convert to int:", code: 'age = int(input("Enter age: "))\nprint(age + 5)   # ✅ Works! Output: 30\n\n# Convert to float:\nprice = float(input("Enter price: "))\nprint(price * 2)  # Works with decimals!' },
            { type: "code", label: "💡 Common Conversions:", code: "# int() - whole numbers\nnum = int(\"10\")     # 10\n\n# float() - decimal numbers  \nprice = float(\"9.99\")  # 9.99\n\n# str() - convert to text\nage = 25\ntext = str(age)    # \"25\"" },
          ],
        },
      },
      {
        id: "5-3", title: "Simple user programs", duration: "5 min",
        content: {
          heading: "Simple User Programs",
          intro: "Let's build real programs that take user input and produce useful output!",
          blocks: [
            { type: "code", label: "🧪 Program 1 — Add Two Numbers:", code: 'num1 = int(input("Enter first number: "))\nnum2 = int(input("Enter second number: "))\n\nresult = num1 + num2\nprint("Sum is:", result)' },
            { type: "code", label: "🧪 Program 2 — User Greeting:", code: 'name = input("Enter your name: ")\ncity = input("Enter your city: ")\nprint("Welcome,", name, "from", city + "!")' },
            { type: "code", label: "🧪 Program 3 — Age Calculator:", code: 'birth_year = int(input("Enter your birth year: "))\nage = 2026 - birth_year\nprint("Your age is:", age, "years")' },
            { type: "code", label: "🧪 Program 4 — Temperature Converter:", code: 'celsius = float(input("Enter temperature in Celsius: "))\nfahrenheit = (celsius * 9/5) + 32\nprint("Temperature in Fahrenheit:", fahrenheit)' },
          ],
        },
      },
    ],
  },
  {
    id: 6, title: "Operators in Python", icon: "🔢",
    subtopics: [
      {
        id: "6-1", title: "Arithmetic operators", duration: "4 min",
        content: {
          heading: "Arithmetic Operators",
          intro: "Operators are symbols used to perform operations on values. Arithmetic operators are for mathematical calculations.",
          blocks: [
            { type: "code", label: "✅ All Arithmetic Operators:", code: "a = 10\nb = 3\n\nprint(a + b)   # 13  — Addition\nprint(a - b)   # 7   — Subtraction\nprint(a * b)   # 30  — Multiplication\nprint(a / b)   # 3.33 — Division (always float)\nprint(a // b)  # 3   — Floor Division\nprint(a % b)   # 1   — Modulus (remainder)\nprint(a ** b)  # 1000 — Power (10 to the 3)" },
            { type: "compare-table", headers: ["Operator", "Meaning", "Example", "Output"],
              rows: [["+", "Addition", "5 + 3", "8"], ["-", "Subtraction", "5 - 3", "2"], ["*", "Multiplication", "5 * 3", "15"], ["/", "Division", "5 / 2", "2.5"], ["//", "Floor Division", "5 // 2", "2"], ["%", "Modulus", "5 % 2", "1"], ["**", "Power", "2 ** 3", "8"]] },
            { type: "tip-box", text: "The % (modulus) operator returns the remainder after division. It's very useful: num % 2 == 0 checks if a number is even!" },
          ],
        },
      },
      {
        id: "6-2", title: "Comparison operators", duration: "4 min",
        content: {
          heading: "Comparison Operators",
          intro: "Comparison operators compare two values and always return True or False (a boolean).",
          blocks: [
            { type: "code", label: "✅ All Comparison Operators:", code: "a = 10\nb = 5\n\nprint(a == b)  # False — Equal to\nprint(a != b)  # True  — Not equal\nprint(a > b)   # True  — Greater than\nprint(a < b)   # False — Less than\nprint(a >= b)  # True  — Greater or equal\nprint(a <= b)  # False — Less or equal" },
            { type: "compare-table", headers: ["Operator", "Meaning", "Example", "Output"],
              rows: [["==", "Equal to", "5 == 5", "True"], ["!=", "Not equal", "5 != 3", "True"], [">", "Greater than", "5 > 3", "True"], ["<", "Less than", "5 < 3", "False"], [">=", "Greater or equal", "5 >= 5", "True"], ["<=", "Less or equal", "3 <= 5", "True"]] },
            { type: "tip-box", text: "CRITICAL: = is assignment (name = 'Amit') and == is comparison (name == 'Amit'). Using = in an if condition is a very common mistake!" },
          ],
        },
      },
      {
        id: "6-3", title: "Logical operators", duration: "4 min",
        content: {
          heading: "Logical Operators — and, or, not",
          intro: "Logical operators combine multiple conditions. They are essential for complex decision-making.",
          blocks: [
            { type: "code", label: "✅ and — Both Must Be True:", code: "age = 20\nprint(age > 18 and age < 25)  # True (both true)\nprint(age > 18 and age > 30)  # False (second is false)" },
            { type: "code", label: "✅ or — At Least One True:", code: "marks = 40\nprint(marks > 35 or marks > 90)  # True (first is true)\nprint(marks > 50 or marks > 90)  # False (both false)" },
            { type: "code", label: "✅ not — Reverses the Result:", code: "print(not True)   # False\nprint(not False)  # True\nprint(not (5 > 3))  # False" },
            { type: "compare-table", headers: ["A", "B", "A and B", "A or B"],
              rows: [["True", "True", "True", "True"], ["True", "False", "False", "True"], ["False", "True", "False", "True"], ["False", "False", "False", "False"]] },
          ],
        },
      },
    ],
  },
  {
    id: 7, title: "Conditional Statements 🔥", icon: "🔀",
    subtopics: [
      {
        id: "7-1", title: "if statement", duration: "4 min",
        content: {
          heading: "The if Statement",
          intro: "Conditional statements allow your program to make decisions. The if statement runs code ONLY when the condition is True.",
          blocks: [
            { type: "code", label: "✅ Basic if Syntax:", code: "# Syntax:\nif condition:\n    # code runs if condition is True\n\n# Example:\nage = 20\nif age >= 18:\n    print(\"You are eligible to vote\")\n# Output: You are eligible to vote" },
            { type: "points", label: "⚠️ Important Rules:", items: ["Colon (:) after the condition is REQUIRED", "Indentation (4 spaces) inside if block is REQUIRED", "Condition must evaluate to True or False", "If condition is False, code inside is skipped"] },
          ],
        },
      },
      {
        id: "7-2", title: "if-else", duration: "4 min",
        content: {
          heading: "if-else Statement",
          intro: "The if-else statement handles two choices: what to do when condition is True, and what to do when it's False.",
          blocks: [
            { type: "code", label: "✅ if-else Syntax:", code: "marks = 30\n\nif marks >= 35:\n    print(\"Pass\")\nelse:\n    print(\"Fail\")\n# Output: Fail" },
            { type: "code", label: "🧪 Real-World Examples:", code: "# Example 1: Voting eligibility\nage = int(input(\"Enter age: \"))\nif age >= 18:\n    print(\"You can vote\")\nelse:\n    print(\"Not eligible yet\")\n\n# Example 2: Even or Odd\nnum = int(input(\"Enter number: \"))\nif num % 2 == 0:\n    print(\"Even number\")\nelse:\n    print(\"Odd number\")" },
          ],
        },
      },
      {
        id: "7-3", title: "if-elif-else", duration: "5 min",
        content: {
          heading: "if-elif-else — Multiple Conditions",
          intro: "When you have more than two choices, use elif (else if). Only ONE block runs — the first condition that is True.",
          blocks: [
            { type: "code", label: "✅ Grading System Example:", code: "marks = 85\n\nif marks >= 90:\n    print(\"Grade A\")\nelif marks >= 75:\n    print(\"Grade B\")\nelif marks >= 50:\n    print(\"Grade C\")\nelse:\n    print(\"Fail\")\n\n# Output: Grade B" },
            { type: "code", label: "🧪 Number Checker:", code: "num = int(input(\"Enter number: \"))\n\nif num > 0:\n    print(\"Positive number\")\nelif num == 0:\n    print(\"Zero\")\nelse:\n    print(\"Negative number\")" },
            { type: "tip-box", text: "Python checks conditions from top to bottom. The FIRST True condition runs, then Python exits the entire if-elif-else block." },
          ],
        },
      },
      {
        id: "7-4", title: "Nested conditions", duration: "4 min",
        content: {
          heading: "Nested Conditions",
          intro: "Nested conditions are if statements inside other if statements — for multi-level decision making.",
          blocks: [
            { type: "code", label: "✅ Nested if Example:", code: "age = 20\nhas_id = True\n\nif age >= 18:\n    print(\"Age requirement met\")\n    if has_id:\n        print(\"Allowed to enter\")\n    else:\n        print(\"Need ID\")\nelse:\n    print(\"Too young\")" },
            { type: "code", label: "🧪 Login System:", code: "username = input(\"Enter username: \")\npassword = input(\"Enter password: \")\n\nif username == \"admin\":\n    if password == \"1234\":\n        print(\"Login successful ✅\")\n    else:\n        print(\"Wrong password ❌\")\nelse:\n    print(\"User not found ❌\")" },
            { type: "tip-box", text: "Avoid too many levels of nesting — it makes code hard to read. Use logical operators (and, or) to simplify when possible!" },
          ],
        },
      },
    ],
  },
  {
    id: 8, title: "Loops in Python", icon: "🔁",
    subtopics: [
      {
        id: "8-1", title: "for loop", duration: "5 min",
        content: {
          heading: "The for Loop",
          intro: "Loops repeat a block of code multiple times. The for loop is used when you know exactly how many times to repeat.",
          blocks: [
            { type: "code", label: "✅ Basic for Loop:", code: "for i in range(5):\n    print(i)\n\n# Output:\n# 0\n# 1\n# 2\n# 3\n# 4" },
            { type: "code", label: "🧪 Loop Through a List:", code: "fruits = [\"apple\", \"banana\", \"mango\"]\n\nfor fruit in fruits:\n    print(fruit)\n\n# Output:\n# apple\n# banana\n# mango" },
            { type: "code", label: "🧪 Real Programs:", code: "# Multiplication table\nnum = int(input(\"Enter number: \"))\nfor i in range(1, 11):\n    print(num, \"x\", i, \"=\", num * i)\n\n# Sum of numbers 1 to 5\ntotal = 0\nfor i in range(1, 6):\n    total += i\nprint(\"Sum:\", total)  # Sum: 15" },
          ],
        },
      },
      {
        id: "8-2", title: "while loop", duration: "4 min",
        content: {
          heading: "The while Loop",
          intro: "The while loop runs as long as a condition is True. Use it when you don't know how many times to repeat.",
          blocks: [
            { type: "code", label: "✅ Basic while Loop:", code: "i = 1\n\nwhile i <= 5:\n    print(i)\n    i += 1  # IMPORTANT: update variable!\n\n# Output: 1 2 3 4 5" },
            { type: "code", label: "⚠️ Infinite Loop — AVOID THIS:", code: "i = 1\nwhile i <= 5:\n    print(i)\n    # ❌ Missing i += 1 → runs forever!\n    # Press Ctrl+C to stop infinite loop" },
            { type: "code", label: "🧪 Password Retry Program:", code: "password = \"\"\n\nwhile password != \"1234\":\n    password = input(\"Enter password: \")\n    if password != \"1234\":\n        print(\"Wrong! Try again\")\n\nprint(\"Access granted ✅\")" },
          ],
        },
      },
      {
        id: "8-3", title: "range()", duration: "4 min",
        content: {
          heading: "The range() Function",
          intro: "range() generates a sequence of numbers for use with for loops. It has three forms.",
          blocks: [
            { type: "code", label: "✅ Three Forms of range():", code: "# 1. Single value: range(stop)\nfor i in range(5):         # 0,1,2,3,4\n    print(i)\n\n# 2. Start & end: range(start, stop)\nfor i in range(1, 6):     # 1,2,3,4,5\n    print(i)\n\n# 3. With step: range(start, stop, step)\nfor i in range(1, 10, 2): # 1,3,5,7,9\n    print(i)" },
            { type: "code", label: "🧪 Even Numbers:", code: "for i in range(2, 11, 2):\n    print(i)\n# Output: 2 4 6 8 10" },
            { type: "tip-box", text: "Remember: range(1, 6) produces 1,2,3,4,5 — it EXCLUDES the stop value! range(5) is same as range(0, 5)." },
          ],
        },
      },
      {
        id: "8-4", title: "Break & continue", duration: "4 min",
        content: {
          heading: "break & continue Statements",
          intro: "break and continue let you control loop execution. break stops the loop entirely, continue skips the current step.",
          blocks: [
            { type: "code", label: "✅ break — Stop Loop Early:", code: "for i in range(1, 6):\n    if i == 3:\n        break        # stop loop when i equals 3\n    print(i)\n\n# Output: 1  2\n# Loop stops at 3 and doesn't continue" },
            { type: "code", label: "✅ continue — Skip Current Iteration:", code: "for i in range(1, 6):\n    if i == 3:\n        continue     # skip 3, keep going\n    print(i)\n\n# Output: 1  2  4  5\n# 3 is skipped, rest continue" },
            { type: "code", label: "🧪 Real Use — Find First Even Number:", code: "for i in range(1, 20):\n    if i % 2 == 0:\n        print(\"First even:\", i)\n        break  # stop after finding first one" },
          ],
        },
      },
    ],
  },
  {
    id: 9, title: "Strings in Python", icon: "📝",
    subtopics: [
      {
        id: "9-1", title: "String operations", duration: "4 min",
        content: {
          heading: "String Operations",
          intro: "A string is a sequence of characters (text). Python provides many powerful ways to work with strings.",
          blocks: [
            { type: "code", label: "✅ Core String Operations:", code: '# Concatenation (joining)\nfirst = "Hello"\nsecond = "World"\nresult = first + " " + second\nprint(result)  # Hello World\n\n# Repetition\nprint("Hi " * 3)  # Hi Hi Hi\n\n# Length\nprint(len("Python"))  # 6\n\n# Check if character exists\nprint("P" in "Python")  # True' },
            { type: "code", label: "🧪 Email Validator:", code: 'email = input("Enter email: ")\nif "@" in email:\n    print("Valid email ✅")\nelse:\n    print("Invalid email ❌")' },
          ],
        },
      },
      {
        id: "9-2", title: "Slicing", duration: "5 min",
        content: {
          heading: "String Indexing & Slicing",
          intro: "Every character in a string has a position (index). You can access any character or extract parts of a string.",
          blocks: [
            { type: "code", label: "📌 String Index Positions:", code: 'text = "Python"\n#      P y t h o n\n#      0 1 2 3 4 5   (positive)\n#     -6-5-4-3-2-1   (negative)\n\nprint(text[0])   # P  (first char)\nprint(text[3])   # h\nprint(text[-1])  # n  (last char)' },
            { type: "code", label: "✅ Slicing — Extract Part of String:", code: 'text = "Python"\n\nprint(text[0:3])   # Pyt  (index 0,1,2)\nprint(text[2:5])   # tho  (index 2,3,4)\nprint(text[:3])    # Pyt  (from start)\nprint(text[3:])    # hon  (to end)\n\n# Step slicing\nprint(text[0:6:2]) # Pto  (every 2nd char)\n\n# Reverse a string!\nprint(text[::-1])  # nohtyP' },
            { type: "tip-box", text: "Slicing rule: string[start:end] — start is INCLUDED, end is EXCLUDED. text[0:3] gives characters at index 0, 1, 2 (not 3)." },
          ],
        },
      },
      {
        id: "9-3", title: "String methods", duration: "5 min",
        content: {
          heading: "String Methods",
          intro: "Python has dozens of built-in string methods. Here are the most important ones every developer uses daily.",
          blocks: [
            { type: "code", label: "✅ Essential String Methods:", code: 'text = "Hello World"\n\n# Change case\nprint(text.upper())          # HELLO WORLD\nprint(text.lower())          # hello world\n\n# Replace text\nprint(text.replace("World", "Python"))  # Hello Python\n\n# Remove extra spaces\nname = "  Amit  "\nprint(name.strip())          # "Amit"\n\n# Split into list\nwords = "apple,banana,mango"\nprint(words.split(","))      # [\'apple\', \'banana\', \'mango\']\n\n# Check content\nprint(text.startswith("Hello"))  # True\nprint(text.find("World"))        # 6 (position)' },
            { type: "code", label: "🧪 Real Programs:", code: '# Format name properly\nname = input("Enter name: ")\nprint("Welcome,", name.upper())\n\n# Replace word\nmsg = "I like Java"\nprint(msg.replace("Java", "Python"))' },
          ],
        },
      },
    ],
  },
  {
    id: 10, title: "Lists in Python", icon: "📋",
    subtopics: [
      {
        id: "10-1", title: "Creating lists", duration: "4 min",
        content: {
          heading: "Creating Lists",
          intro: "A list stores multiple values in a single variable. It is one of the most used data structures in Python.",
          blocks: [
            { type: "code", label: "✅ Different Ways to Create Lists:", code: '# Basic list of numbers\nnumbers = [1, 2, 3, 4, 5]\n\n# List of strings\nfruits = ["apple", "banana", "mango"]\n\n# Mixed data types\ndata = ["Amit", 25, 5.8, True]\n\n# Empty list\nitems = []\n\n# Using list() function\nnums = list((1, 2, 3))' },
            { type: "points", label: "📌 Key Features of Lists:", items: ["Ordered — items have fixed positions (index)", "Mutable — you can add, remove, and change items", "Allows duplicates — [1, 1, 2, 2] is valid", "Can store any data type — even other lists!"] },
          ],
        },
      },
      {
        id: "10-2", title: "Indexing & slicing", duration: "4 min",
        content: {
          heading: "List Indexing & Slicing",
          intro: "Access any element in a list using its index. Lists work exactly like strings for indexing and slicing.",
          blocks: [
            { type: "code", label: "✅ Indexing:", code: 'fruits = ["apple", "banana", "mango"]\n#          0         1          2\n#         -3        -2         -1\n\nprint(fruits[0])   # apple\nprint(fruits[1])   # banana\nprint(fruits[-1])  # mango (last item)' },
            { type: "code", label: "✅ Slicing:", code: 'nums = [10, 20, 30, 40, 50]\n\nprint(nums[0:3])   # [10, 20, 30]\nprint(nums[2:5])   # [30, 40, 50]\nprint(nums[::2])   # [10, 30, 50] (every 2nd)\nprint(nums[::-1])  # [50, 40, 30, 20, 10] (reverse)' },
            { type: "code", label: "✅ Modifying Elements:", code: 'fruits = ["apple", "banana", "mango"]\nfruits[1] = "orange"  # change banana to orange\nprint(fruits)  # ["apple", "orange", "mango"]' },
          ],
        },
      },
      {
        id: "10-3", title: "Methods (append, remove)", duration: "5 min",
        content: {
          heading: "List Methods",
          intro: "Python provides powerful built-in methods to add, remove, sort, and manipulate list elements.",
          blocks: [
            { type: "code", label: "✅ Essential List Methods:", code: 'fruits = ["apple", "banana"]\n\n# append() — add to end\nfruits.append("mango")\nprint(fruits)  # [\'apple\', \'banana\', \'mango\']\n\n# remove() — remove by value\nfruits.remove("banana")\nprint(fruits)  # [\'apple\', \'mango\']\n\n# pop() — remove last item\nfruits.pop()\n\n# sort() — sort alphabetically/numerically\nnums = [3, 1, 4, 1, 5]\nnums.sort()\nprint(nums)  # [1, 1, 3, 4, 5]\n\n# len() — count items\nprint(len(fruits))  # number of items' },
            { type: "code", label: "🧪 Loop Through List:", code: 'fruits = ["apple", "banana", "mango"]\n\nfor item in fruits:\n    print(item)\n\n# Output:\n# apple\n# banana\n# mango' },
          ],
        },
      },
    ],
  },
  {
    id: 11, title: "Tuples & Sets", icon: "🔒",
    subtopics: [
      {
        id: "11-1", title: "Tuple (immutable list)", duration: "4 min",
        content: {
          heading: "Tuples — Immutable Lists",
          intro: "A tuple is like a list, but CANNOT be changed after creation. It is read-only — immutable.",
          blocks: [
            { type: "code", label: "✅ Tuple Examples:", code: 'numbers = (1, 2, 3)       # with parentheses\ncolors = ("red", "green", "blue")\ncoordinates = (10, 20)    # x, y position\n\n# Access elements (same as list)\nnums = (10, 20, 30)\nprint(nums[0])   # 10\nprint(nums[-1])  # 30' },
            { type: "code", label: "❌ Cannot Modify Tuple:", code: 'nums = (1, 2, 3)\nnums[0] = 10  # ❌ TypeError: tuple is immutable\n\n# Tuples are READ-ONLY after creation' },
            { type: "points", label: "🧠 When to Use Tuple:", items: ["Fixed data — days of week, months, coordinates", "Faster than lists (better performance)", "Safer — prevents accidental data changes", "Use as dictionary keys (lists cannot be keys)"] },
          ],
        },
      },
      {
        id: "11-2", title: "Set (unique values)", duration: "4 min",
        content: {
          heading: "Sets — Unique Values Only",
          intro: "A set is a collection of unique values. Duplicates are automatically removed. Sets are unordered.",
          blocks: [
            { type: "code", label: "✅ Set Examples:", code: "numbers = {1, 2, 3, 3, 4, 4}\nprint(numbers)  # {1, 2, 3, 4}  — duplicates removed!\n\n# Adding elements\nnums = {1, 2, 3}\nnums.add(4)\nprint(nums)  # {1, 2, 3, 4}\n\n# Removing elements\nnums.remove(2)\nprint(nums)  # {1, 3, 4}" },
            { type: "code", label: "🔥 Set Operations:", code: "a = {1, 2, 3}\nb = {3, 4, 5}\n\nprint(a | b)  # Union: {1,2,3,4,5}\nprint(a & b)  # Intersection: {3}\nprint(a - b)  # Difference: {1,2}" },
            { type: "code", label: "🧪 Remove Duplicates from List:", code: "nums = [1, 2, 2, 3, 4, 4, 5]\nunique_nums = set(nums)\nprint(unique_nums)  # {1, 2, 3, 4, 5}" },
          ],
        },
      },
      {
        id: "11-3", title: "List vs Tuple vs Set", duration: "4 min",
        content: {
          heading: "List vs Tuple vs Set — Comparison",
          intro: "Knowing when to use each data structure is a key skill for every Python developer.",
          blocks: [
            { type: "compare-table", headers: ["Feature", "List []", "Tuple ()", "Set {}"],
              rows: [["Ordered", "✅ Yes", "✅ Yes", "❌ No"], ["Mutable", "✅ Yes", "❌ No", "✅ Yes"], ["Duplicates", "✅ Yes", "✅ Yes", "❌ No"], ["Indexing", "✅ Yes", "✅ Yes", "❌ No"], ["Speed", "Medium", "Fast", "Fast"]] },
            { type: "code", label: "✅ Same Data — Different Results:", code: "list1  = [1, 2, 2, 3]  # keeps duplicates\ntuple1 = (1, 2, 2, 3)  # keeps duplicates\nset1   = {1, 2, 2, 3}  # removes duplicates\n\nprint(list1)   # [1, 2, 2, 3]\nprint(tuple1)  # (1, 2, 2, 3)\nprint(set1)    # {1, 2, 3}" },
            { type: "tip-box", text: "Rule of thumb: Use list for ordered changeable data, tuple for fixed data, set when you need unique values." },
          ],
        },
      },
    ],
  },
  {
    id: 12, title: "Dictionaries", icon: "📖",
    subtopics: [
      {
        id: "12-1", title: "Key-value pairs", duration: "4 min",
        content: {
          heading: "Dictionary — Key-Value Pairs",
          intro: "A dictionary stores data in key-value pairs — like a real dictionary where words (keys) have definitions (values).",
          blocks: [
            { type: "code", label: "✅ Creating a Dictionary:", code: 'student = {\n    "name": "Amit",\n    "age": 25,\n    "city": "Ahmedabad",\n    "marks": 85\n}\n\n# Mixed value types\ndata = {\n    "name": "Amit",\n    "marks": [80, 90, 85],   # list as value\n    "passed": True           # boolean as value\n}' },
            { type: "points", label: "📌 Key Rules:", items: ["Keys must be unique — no two same keys", "Values can be anything — string, int, list, dict", "Keys are usually strings or numbers", "Data is mutable — you can add, change, remove"] },
          ],
        },
      },
      {
        id: "12-2", title: "Accessing values", duration: "4 min",
        content: {
          heading: "Accessing Dictionary Values",
          intro: "Access dictionary values using their keys — not index numbers like lists.",
          blocks: [
            { type: "code", label: "✅ Access by Key:", code: 'student = {"name": "Amit", "age": 25}\n\nprint(student["name"])  # Amit\nprint(student["age"])   # 25\n\n# ❌ Key not found → KeyError!\nprint(student["city"])  # KeyError!\n\n# ✅ Safe access with get()\nprint(student.get("city"))         # None (no error)\nprint(student.get("city", "N/A")) # N/A (default)' },
            { type: "code", label: "✅ Modifying Dictionary:", code: 'student = {"name": "Amit", "age": 25}\n\n# Add new key\nstudent["city"] = "Ahmedabad"\n\n# Update value\nstudent["age"] = 26\n\n# Remove key\nstudent.pop("age")\n\nprint(student)' },
          ],
        },
      },
      {
        id: "12-3", title: "Dictionary methods", duration: "5 min",
        content: {
          heading: "Dictionary Methods",
          intro: "Python provides many useful built-in methods for working with dictionaries.",
          blocks: [
            { type: "code", label: "✅ Essential Methods:", code: 'student = {"name": "Amit", "age": 25, "city": "Ahmedabad"}\n\n# keys() — get all keys\nprint(student.keys())    # dict_keys([\'name\',\'age\',\'city\'])\n\n# values() — get all values\nprint(student.values())  # dict_values([\'Amit\', 25, \'Ahmedabad\'])\n\n# items() — get key-value pairs\nprint(student.items())   # dict_items([(\'name\', \'Amit\')...])\n\n# update() — add/update multiple keys\nstudent.update({"marks": 90, "grade": "A"})\n\n# Loop through dictionary\nfor key, value in student.items():\n    print(key, ":", value)' },
            { type: "code", label: "🧪 Real-World Dictionary:", code: 'users = {\n    "user1": {"name": "Amit", "age": 25},\n    "user2": {"name": "Rahul", "age": 22}\n}\n\nprint(users["user1"]["name"])  # Amit' },
          ],
        },
      },
    ],
  },
  {
    id: 13, title: "Functions (Basics)", icon: "⚡",
    subtopics: [
      {
        id: "13-1", title: "def keyword", duration: "4 min",
        content: {
          heading: "Defining Functions with def",
          intro: "A function is a reusable block of code that performs a specific task. Think of it like a machine: Input → Process → Output.",
          blocks: [
            { type: "code", label: "✅ Creating and Calling a Function:", code: '# Define function\ndef greet():\n    print("Hello, welcome!")\n\n# Call function (run it)\ngreet()\n# Output: Hello, welcome!\n\n# Call it multiple times!\ngreet()\ngreet()' },
            { type: "points", label: "✅ Why Use Functions?", items: ["Avoid repeating code — write once, use many times", "Make code clean and organized", "Easier to debug — fix in one place", "Split complex programs into smaller tasks"] },
          ],
        },
      },
      {
        id: "13-2", title: "Parameters & arguments", duration: "5 min",
        content: {
          heading: "Parameters & Arguments",
          intro: "Parameters let you pass data INTO a function. They make functions flexible and reusable.",
          blocks: [
            { type: "code", label: "✅ Function with Parameters:", code: '# name is the PARAMETER\ndef greet(name):\n    print("Hello", name)\n\n# "Amit" is the ARGUMENT\ngreet("Amit")   # Hello Amit\ngreet("Rahul")  # Hello Rahul' },
            { type: "code", label: "✅ Multiple Parameters:", code: 'def add(a, b):\n    print(a + b)\n\nadd(5, 3)   # 8\nadd(10, 20) # 30' },
            { type: "code", label: "✅ Default Parameters:", code: 'def greet(name="Guest"):\n    print("Hello", name)\n\ngreet()          # Hello Guest  (uses default)\ngreet("Amit")    # Hello Amit   (uses argument)' },
            { type: "compare-table", headers: ["Term", "Meaning", "Example"],
              rows: [["Parameter", "Variable in function definition", "def greet(name):"], ["Argument", "Actual value passed when calling", "greet('Amit')"]] },
          ],
        },
      },
      {
        id: "13-3", title: "Return values", duration: "4 min",
        content: {
          heading: "Return Values",
          intro: "The return statement sends a value back from a function. Without return, the function returns None.",
          blocks: [
            { type: "code", label: "✅ Function with Return:", code: 'def add(a, b):\n    return a + b\n\n# Store the returned value\nresult = add(5, 3)\nprint(result)   # 8\n\n# Use directly\nprint(add(10, 20))  # 30' },
            { type: "code", label: "🧪 Practical Examples:", code: 'def square(num):\n    return num * num\n\ndef check_even(num):\n    if num % 2 == 0:\n        return "Even"\n    else:\n        return "Odd"\n\nprint(square(4))       # 16\nprint(check_even(7))   # Odd' },
            { type: "tip-box", text: "return ends function execution immediately. Any code written after return will NEVER run!" },
          ],
        },
      },
    ],
  },
  {
    id: 14, title: "Error Handling (Basics)", icon: "🛡️",
    subtopics: [
      {
        id: "14-1", title: "Common errors", duration: "4 min",
        content: {
          heading: "Common Python Errors",
          intro: "Every programmer encounters errors. Understanding what each error means helps you fix them quickly.",
          blocks: [
            { type: "code", label: "✅ Most Common Error Types:", code: '# SyntaxError — wrong code format\nprint("Hello"   # Missing bracket\n\n# NameError — variable not defined\nprint(name)     # name not created yet\n\n# TypeError — wrong data type\nprint("10" + 5) # Cannot add str + int\n\n# ValueError — invalid value\nint("abc")      # Cannot convert text to int\n\n# ZeroDivisionError — divide by zero\nprint(10 / 0)   # Math error!' },
            { type: "compare-table", headers: ["Error Type", "When It Happens", "Example"],
              rows: [["SyntaxError", "Wrong code format/grammar", "Missing bracket or colon"], ["NameError", "Variable not defined", "Using variable before creating it"], ["TypeError", "Wrong data type operation", "String + Integer"], ["ValueError", "Invalid conversion", 'int("abc")'], ["ZeroDivisionError", "Divide by zero", "10 / 0"]] },
          ],
        },
      },
      {
        id: "14-2", title: "try-except", duration: "5 min",
        content: {
          heading: "try-except — Handle Errors Safely",
          intro: "try-except lets you catch errors and handle them gracefully — so your program doesn't crash!",
          blocks: [
            { type: "code", label: "✅ Basic try-except:", code: 'try:\n    num = int(input("Enter number: "))\n    print(10 / num)\nexcept:\n    print("An error occurred!")\n\n# If user enters 0 or text → program continues safely!' },
            { type: "code", label: "✅ Handle Specific Errors (Best Practice):", code: 'try:\n    num = int(input("Enter number: "))\n    print(10 / num)\n\nexcept ValueError:\n    print("Please enter a valid number")\n\nexcept ZeroDivisionError:\n    print("Cannot divide by zero")' },
            { type: "code", label: "✅ else and finally:", code: 'try:\n    num = int(input("Enter number: "))\nexcept:\n    print("Invalid input")\nelse:\n    print("You entered:", num)  # runs if NO error\nfinally:\n    print("Done!")  # ALWAYS runs (error or not)' },
          ],
        },
      },
      {
        id: "14-3", title: "Debugging basics", duration: "4 min",
        content: {
          heading: "Debugging Basics",
          intro: "Debugging = finding and fixing errors. Every professional developer spends time debugging. Here are the key techniques.",
          blocks: [
            { type: "steps", label: "🟢 Debugging Methods:", items: ["Read the error message — Python tells you EXACTLY what went wrong and on which line", "Use print() — add print() statements to check variable values at each step", "Test step-by-step — break your code into smaller parts and test each separately", "Use VS Code Debugger — set breakpoints and inspect variables visually"] },
            { type: "code", label: "🧪 Debugging with print():", code: 'num = input("Enter number: ")\nprint("After input:", num, type(num))  # debug\n\nnum = int(num)\nprint("After convert:", num, type(num))  # debug\n\nresult = num * 2\nprint("Result:", result)' },
            { type: "tip-box", text: "Pro Tip: When you get an error, read the LAST line of the error message first — that's the actual error. The lines above show where it happened." },
          ],
        },
      },
    ],
  },
  {
    id: 15, title: "Mini Projects 🔥", icon: "🚀",
    subtopics: [
      {
        id: "15-1", title: "Calculator", duration: "6 min",
        content: {
          heading: "Project 1 — Basic Calculator",
          intro: "Build a calculator that takes two numbers and an operator, then performs the calculation. Concepts: input, type casting, operators, conditions.",
          blocks: [
            { type: "code", label: "✅ Complete Calculator Code:", code: 'num1 = float(input("Enter first number: "))\nnum2 = float(input("Enter second number: "))\n\nop = input("Enter operator (+, -, *, /): ")\n\nif op == "+":\n    print("Result:", num1 + num2)\nelif op == "-":\n    print("Result:", num1 - num2)\nelif op == "*":\n    print("Result:", num1 * num2)\nelif op == "/":\n    if num2 != 0:\n        print("Result:", num1 / num2)\n    else:\n        print("Cannot divide by zero")\nelse:\n    print("Invalid operator")' },
            { type: "points", label: "🧠 What You Practice:", items: ["User input & type casting", "Conditional statements (if-elif-else)", "Arithmetic operators", "Edge case handling (division by zero)"] },
          ],
        },
      },
      {
        id: "15-2", title: "Number guessing game", duration: "6 min",
        content: {
          heading: "Project 2 — Number Guessing Game",
          intro: "Build an interactive game where Python picks a secret number and the user tries to guess it. Concepts: loops, conditions, random module.",
          blocks: [
            { type: "code", label: "✅ Complete Game Code:", code: 'import random\n\nsecret = random.randint(1, 10)\n\nwhile True:\n    guess = int(input("Guess a number (1-10): "))\n\n    if guess == secret:\n        print("Correct! 🎉 You won!")\n        break\n    elif guess < secret:\n        print("Too low! Try higher")\n    else:\n        print("Too high! Try lower")' },
            { type: "points", label: "🧠 What You Practice:", items: ["Importing and using modules (random)", "while loop with break", "if-elif-else conditions", "Game logic and user experience"] },
          ],
        },
      },
      {
        id: "15-3", title: "Simple login system", duration: "4 min",
        content: {
          heading: "Project 3 — Simple Login System",
          intro: "Build a login system that validates username and password. Concepts: strings, conditions, logical operators.",
          blocks: [
            { type: "code", label: "✅ Login System Code:", code: 'username = input("Enter username: ")\npassword = input("Enter password: ")\n\nif username == "admin" and password == "1234":\n    print("Login successful ✅")\n    print("Welcome, Admin!")\nelse:\n    print("Invalid credentials ❌")\n    print("Please try again")' },
            { type: "code", label: "🚀 Upgraded Version — 3 Attempts:", code: 'attempts = 3\n\nwhile attempts > 0:\n    username = input("Username: ")\n    password = input("Password: ")\n\n    if username == "admin" and password == "1234":\n        print("Login successful ✅")\n        break\n    else:\n        attempts -= 1\n        print(f"Wrong! {attempts} attempts left")\n\nif attempts == 0:\n    print("Account locked 🔒")' },
          ],
        },
      },
      {
        id: "15-4", title: "Even/Odd checker", duration: "3 min",
        content: {
          heading: "Project 4 — Even/Odd Checker",
          intro: "A simple but important program that checks if a number is even or odd using the modulus operator.",
          blocks: [
            { type: "code", label: "✅ Basic Even/Odd:", code: 'num = int(input("Enter a number: "))\n\nif num % 2 == 0:\n    print(f"{num} is an Even number")\nelse:\n    print(f"{num} is an Odd number")' },
            { type: "code", label: "🚀 Upgraded — Check Multiple Numbers:", code: 'while True:\n    user_input = input("Enter number (or \'quit\' to stop): ")\n\n    if user_input == "quit":\n        print("Goodbye!")\n        break\n\n    num = int(user_input)\n    if num % 2 == 0:\n        print(f"{num} → Even ✅")\n    else:\n        print(f"{num} → Odd 🔵")' },
            { type: "tip-box", text: "The % (modulus) operator is the key: if num % 2 == 0, the number divides evenly by 2 → it's EVEN!" },
          ],
        },
      },
    ],
  },
  {
    id: 16, title: "Final Beginner Project 💡", icon: "🏆",
    subtopics: [
      {
        id: "16-1", title: "Student Marks Calculator", duration: "8 min",
        content: {
          heading: "Final Project 1 — Student Marks Calculator",
          intro: "This project combines ALL topics you've learned. Take marks from multiple subjects, calculate total, average, and grade.",
          blocks: [
            { type: "code", label: "✅ Complete Student Marks Calculator:", code: 'def calculate_grade(avg):\n    if avg >= 90:\n        return "A"\n    elif avg >= 75:\n        return "B"\n    elif avg >= 50:\n        return "C"\n    else:\n        return "Fail"\n\n\nmarks = []\nn = int(input("Enter number of subjects: "))\n\nfor i in range(n):\n    m = float(input(f"Enter marks for subject {i+1}: "))\n    marks.append(m)\n\ntotal = sum(marks)\naverage = total / n\ngrade = calculate_grade(average)\n\nprint("\\n===== RESULT =====")\nprint("Total Marks:", total)\nprint("Average:", round(average, 2))\nprint("Grade:", grade)' },
            { type: "points", label: "🧠 Concepts Used:", items: ["Functions with parameters and return values", "Lists — storing multiple marks", "for loop — collecting input", "Conditional statements — grading logic", "Built-in sum() function"] },
          ],
        },
      },
      {
        id: "16-2", title: "To-Do List CLI App", duration: "10 min",
        content: {
          heading: "Final Project 2 — To-Do List App",
          intro: "Build a complete command-line To-Do list application with add, view, and remove features. This is a real-world app!",
          blocks: [
            { type: "code", label: "✅ Complete To-Do List App:", code: 'tasks = []\n\ndef show_tasks():\n    if len(tasks) == 0:\n        print("No tasks yet!")\n    else:\n        for i, task in enumerate(tasks):\n            print(f"{i + 1}. {task}")\n\n\nwhile True:\n    print("\\n===== TO-DO APP =====")\n    print("1. Add Task")\n    print("2. View Tasks")\n    print("3. Remove Task")\n    print("4. Exit")\n\n    choice = input("Enter choice: ")\n\n    if choice == "1":\n        task = input("Enter task: ")\n        tasks.append(task)\n        print("✅ Task added!")\n\n    elif choice == "2":\n        show_tasks()\n\n    elif choice == "3":\n        show_tasks()\n        num = int(input("Enter task number to remove: "))\n        if 0 < num <= len(tasks):\n            tasks.pop(num - 1)\n            print("🗑️ Task removed!")\n        else:\n            print("Invalid number")\n\n    elif choice == "4":\n        print("Goodbye! 👋")\n        break\n\n    else:\n        print("Invalid choice!")' },
            { type: "points", label: "🧠 All Concepts Combined:", items: ["Lists — storing tasks", "Functions — show_tasks() function", "while loop — keep app running", "if-elif-else — menu navigation", "enumerate() — numbered display", "Error handling — invalid input check"] },
            { type: "tip-box", text: "🎉 Congratulations! You've completed the Basic Python Course! You can now write real Python programs and are ready for Intermediate Python!" },
          ],
        },
      },
    ],
  },
];

/* ═══════════════════════════════════════════════════════
   CONTENT BLOCKS RENDERER
═══════════════════════════════════════════════════════ */
function ContentBlocks({ blocks }) {
  return (
    <div className="py-blocks">
      {blocks.map((block, i) => {
        if (block.type === "points") return (
          <div key={i} className="b-points">
            {block.label && <p className="b-label">{block.label}</p>}
            <ul>{block.items.map((item, j) => <li key={j}><span className="b-arrow">▸</span>{item}</li>)}</ul>
          </div>
        );

        if (block.type === "card-grid") return (
          <div key={i} className="b-cards">
            {block.items.map((c, j) => (
              <div key={j} className="b-card">
                <span className="b-card-emoji">{c.emoji}</span>
                <h4>{c.title}</h4>
                <p>{c.desc}</p>
              </div>
            ))}
          </div>
        );

        if (block.type === "code") return (
          <div key={i} className="b-code-wrap">
            {block.label && <p className="b-label">{block.label}</p>}
            <div className="b-code-header">
              <span className="dot d1" /><span className="dot d2" /><span className="dot d3" />
              <span className="b-code-lang">Python</span>
            </div>
            <pre className="b-code"><code>{block.code}</code></pre>
          </div>
        );

        if (block.type === "compare-table") return (
          <div key={i} className="b-table-wrap">
            {block.label && <p className="b-label">{block.label}</p>}
            <table className="b-table">
              <thead><tr>{block.headers.map((h, j) => <th key={j}>{h}</th>)}</tr></thead>
              <tbody>{block.rows.map((row, j) => <tr key={j}>{row.map((cell, k) => <td key={k}>{cell}</td>)}</tr>)}</tbody>
            </table>
          </div>
        );

        if (block.type === "steps") return (
          <div key={i} className="b-steps">
            {block.label && <p className="b-label">{block.label}</p>}
            <ol>{block.items.map((s, j) => <li key={j}><span className="step-n">{j + 1}</span>{s}</li>)}</ol>
          </div>
        );

        if (block.type === "tip-box") return (
          <div key={i} className="b-tip">
            <span className="tip-bulb">💡</span>
            <span>{block.text}</span>
          </div>
        );

        return null;
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
═══════════════════════════════════════════════════════ */
export default function BasicPythonCourse() {
  useSEO({
    title: "Python Programming Basics — Free Beginner Tutorial | NextSkillAI",
    description:
      "Learn Python from scratch with structured lessons on variables, loops, functions, lists, and more. Free beginner-friendly Python course on NextSkillAI.",
    keywords:
      "Python tutorial, Python basics, learn Python, Python for beginners, Python programming, NextSkillAI Python course, variables loops functions",
    canonical: "/tutorials/python-basics",
    schema: {
      "@context": "https://schema.org",
      "@type": "Course",
      name: "Python Programming Basics",
      description:
        "A structured beginner Python course covering variables, loops, functions, and practical coding foundations.",
      provider: {
        "@type": "Organization",
        name: "NextSkillAI",
        url: "https://nextskillai.com",
      },
      url: "https://nextskillai.com/tutorials/python-basics",
      educationalLevel: "Beginner",
      inLanguage: "en",
    },
  });

  const [expandedTopics, setExpandedTopics] = useState({ 1: true });
  const [activeSub, setActiveSub] = useState("1-1");
  const mainRef = useRef(null);

  const toggleTopic = (id) => setExpandedTopics(p => ({ ...p, [id]: !p[id] }));

  const selectSub = (topicId, subId) => {
    setExpandedTopics(p => ({ ...p, [topicId]: true }));
    setActiveSub(subId);
    if (mainRef.current) mainRef.current.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Find active content
  let activeTopic = null, activeSubData = null;
  for (const topic of COURSE) {
    const sub = topic.subtopics.find(s => s.id === activeSub);
    if (sub) { activeTopic = topic; activeSubData = sub; break; }
  }

  // Flat navigation list
  const flatSubs = COURSE.flatMap(t => t.subtopics.map(s => ({ ...s, topicId: t.id })));
  const flatIdx = flatSubs.findIndex(s => s.id === activeSub);

  const goNext = () => { if (flatIdx < flatSubs.length - 1) selectSub(flatSubs[flatIdx + 1].topicId, flatSubs[flatIdx + 1].id); };
  const goPrev = () => { if (flatIdx > 0) selectSub(flatSubs[flatIdx - 1].topicId, flatSubs[flatIdx - 1].id); };

  const progress = Math.round(((flatIdx + 1) / flatSubs.length) * 100);

  return (
    <div className="py-page">

      {/* ══════ LEFT SIDEBAR ══════ */}
      <aside className="py-sidebar">
        <div className="py-sb-head">
          <div className="py-sb-brand">
            <span className="py-sb-logo">🐍</span>
            <div>
              <div className="py-sb-title">Python Course</div>
              <div className="py-sb-sub">{flatIdx + 1} of {flatSubs.length} lessons</div>
            </div>
          </div>
          <div className="py-progress-bar">
            <div className="py-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <div className="py-progress-label">{progress}% Complete</div>
        </div>

        <nav className="py-sb-nav">
          {COURSE.map((topic) => {
            const isActive = topic.subtopics.some(s => s.id === activeSub);
            const isOpen = expandedTopics[topic.id];
            return (
              <div key={topic.id} className="py-topic-group">
                <button
                  className={`py-topic-btn ${isActive ? "py-topic-active" : ""}`}
                  onClick={() => toggleTopic(topic.id)}
                >
                  <span className="py-t-icon">{topic.icon}</span>
                  <span className="py-t-label">{topic.id}. {topic.title}</span>
                  <span className={`py-t-arrow ${isOpen ? "open" : ""}`}>›</span>
                </button>

                {isOpen && (
                  <div className="py-sub-list">
                    {topic.subtopics.map(sub => {
                      const isSub = activeSub === sub.id;
                      return (
                        <button
                          key={sub.id}
                          className={`py-sub-btn ${isSub ? "py-sub-active" : ""}`}
                          onClick={() => selectSub(topic.id, sub.id)}
                        >
                          <span className={`py-sub-dot ${isSub ? "dot-on" : ""}`} />
                          <span className="py-sub-label">{sub.title}</span>
                          <span className="py-sub-dur">{sub.duration}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>

      {/* ══════ MAIN CONTENT ══════ */}
      <main className="py-main" ref={mainRef}>

        {/* Breadcrumb */}
        <div className="py-breadcrumb">
          <span>Courses</span>
          <span className="py-bc-sep">›</span>
          <span>Python</span>
          <span className="py-bc-sep">›</span>
          <span className="py-bc-cur">{activeTopic?.title}</span>
        </div>

        {activeSubData && (
          <article className="py-article">

            {/* Header */}
            <div className="py-art-header">
              <span className="py-art-icon">{activeTopic?.icon}</span>
              <div>
                <h1>{activeSubData.content.heading}</h1>
                <div className="py-art-meta">
                  <span>⏱ {activeSubData.duration}</span>
                  <span>📖 Lesson {flatIdx + 1} of {flatSubs.length}</span>
                  <span className="py-art-tag">{activeTopic?.title}</span>
                </div>
              </div>
            </div>

            {/* Intro */}
            {activeSubData.content.intro && (
              <div className="py-intro">
                <p>{activeSubData.content.intro}</p>
              </div>
            )}

            {/* Blocks */}
            {activeSubData.content.blocks && (
              <ContentBlocks blocks={activeSubData.content.blocks} />
            )}

            {/* Navigation */}
            <div className="py-art-nav">
              <button className="py-nav-btn py-nav-prev" disabled={flatIdx === 0} onClick={goPrev}>
                ◀ Previous
              </button>
              <span className="py-nav-count">{flatIdx + 1} / {flatSubs.length}</span>
              <button className="py-nav-btn py-nav-next" disabled={flatIdx === flatSubs.length - 1} onClick={goNext}>
                Next ▶
              </button>
            </div>
          </article>
        )}
      </main>

      {/* ══════ RIGHT ADS COLUMN ══════ */}
      <aside className="py-ads">
        <div className="py-ad-unit">
          <span className="py-ad-label">Advertisement</span>
          <div className="py-ad-box">
            <span className="py-ad-icon">📢</span>
            <p>Your AD Here</p>
            <span className="py-ad-size">300×250</span>
          </div>
        </div>

        <div className="py-ad-unit">
          <span className="py-ad-label">Advertisement</span>
          <div className="py-ad-box">
            <span className="py-ad-icon">📢</span>
            <p>Your AD Here</p>
            <span className="py-ad-size">300×250</span>
          </div>
        </div>

        <div className="py-ad-sticky">
          <div className="py-ad-unit">
            <span className="py-ad-label">Advertisement</span>
            <div className="py-ad-box py-ad-tall">
              <span className="py-ad-icon">📢</span>
              <p>Your AD</p>
              <span className="py-ad-size">300×600</span>
            </div>
          </div>
        </div>
      </aside>

    </div>
  );
}